import { FC, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import type { AgentCoreAttachment } from '@/shared/api/agentCore/agentCoreApi';
import api from '@/shared/api/api';
import { getFileSrc } from '@/shared/lib';
import { Spinner, Modal } from '@/shared/ui';

import styles from './AttachmentViewer.module.scss';

/**
 * Chooses how one attachment can be previewed.
 * - image/* -> rendered as an image
 * - application/pdf (by mime or .pdf extension) -> embedded frame
 * - text/* and common text extensions -> fetched and shown as text
 * - everything else -> download link only
 */
export const attachmentViewerKind = (
  mime: string,
  name: string,
): 'image' | 'pdf' | 'text' | 'other' => {
  const normalized = (mime || '').toLowerCase();
  if (normalized.startsWith('image/')) {
    return 'image';
  }
  if (normalized === 'application/pdf') {
    return 'pdf';
  }
  if (normalized.startsWith('text/')) {
    return 'text';
  }
  const extension = name.split('.').pop()?.toLowerCase() ?? '';
  if (extension === 'pdf') {
    return 'pdf';
  }
  if (
    ['txt', 'md', 'markdown', 'csv', 'json', 'xml', 'log', 'html'].includes(
      extension,
    )
  ) {
    return 'text';
  }
  return 'other';
};

type Props = {
  attachment: AgentCoreAttachment | null;
  onClose: () => void;
};

export const AttachmentViewer: FC<Props> = ({ attachment, onClose }) => {
  const [fileID, setFileID] = useState<string | null>(null);
  // Which asset the current fileID belongs to: effects in the same commit
  // still see the previous attachment's state, so content fetches are gated
  // on this — without it, switching attachments flashes the stale file.
  const [loadedAssetID, setLoadedAssetID] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const [pdfURL, setPdfURL] = useState('');

  useEffect(() => {
    setFileID(null);
    setLoadedAssetID('');
    setLoadError(false);
    setText(null);
    if (!attachment) {
      return;
    }
    let active = true;
    void api.assets
      .getChatUploadAsset({ AssetID: attachment.assetID })
      .then((response) => {
        const kind = response.Asset?.Kind;
        if (active && kind?.case === 'ChatUpload') {
          const content = kind.value.Content;
          if (content?.case === 'File') {
            setFileID(content.value.File?.ID ?? null);
            setLoadedAssetID(attachment.assetID);
          }
        }
      })
      .catch(() => {
        if (active) {
          setLoadError(true);
        }
      })
      .finally(() => {
        if (active && !attachment) {
          return;
        }
      });
    return () => {
      active = false;
    };
  }, [attachment]);

  const kind = useMemo(
    () =>
      attachment
        ? attachmentViewerKind(attachment.mime, attachment.name)
        : 'other',
    [attachment],
  );

  const fileURL = useMemo(() => (fileID ? getFileSrc(fileID) : ''), [fileID]);

  useEffect(() => {
    if (
      !attachment ||
      !fileID ||
      loadedAssetID !== attachment.assetID ||
      kind !== 'text'
    ) {
      return;
    }
    let active = true;
    setText(null);
    void fetch(fileURL)
      .then((response) =>
        response.ok
          ? response.text()
          : Promise.reject(new Error('load failed')),
      )
      .then((content) => {
        if (active) {
          setText(content);
        }
      })
      .catch(() => {
        if (active) {
          setLoadError(true);
        }
      });
    return () => {
      active = false;
    };
  }, [attachment, fileID, kind, fileURL, loadedAssetID]);

  // PDFs are fetched as blobs and rendered from an object URL. A blob URL has
  // no Content-Disposition, so the file always renders in the frame even when
  // the server would otherwise force a download (e.g. report-generated PDFs).
  useEffect(() => {
    if (
      !attachment ||
      !fileID ||
      loadedAssetID !== attachment.assetID ||
      kind !== 'pdf'
    ) {
      // Left the PDF view (other kind, closed, unmounted): drop the URL so
      // the revoke effect below releases the blob instead of leaking it.
      setPdfURL('');
      return;
    }
    let active = true;
    setPdfURL('');
    setLoadError(false);
    void fetch(fileURL)
      .then((response) =>
        response.ok
          ? response.blob()
          : Promise.reject(new Error('load failed')),
      )
      .then((blob) => {
        if (!active) {
          return;
        }
        // The served type may be generic; normalize so the frame renders.
        const pdfBlob =
          blob.type === 'application/pdf'
            ? blob
            : new Blob([blob], { type: 'application/pdf' });
        setPdfURL(URL.createObjectURL(pdfBlob));
      })
      .catch(() => {
        if (active) {
          setLoadError(true);
        }
      });
    return () => {
      active = false;
    };
  }, [attachment, fileID, kind, fileURL, loadedAssetID]);

  useEffect(() => {
    if (!pdfURL) {
      return;
    }
    return () => URL.revokeObjectURL(pdfURL);
  }, [pdfURL]);

  const url = fileID ? getFileSrc(fileID) : '';
  const loading = Boolean(attachment) && !fileID && !loadError;
  const pdfLoading =
    kind === 'pdf' && Boolean(attachment) && !pdfURL && !loadError;

  return (
    <Modal
      isOpen={Boolean(attachment)}
      onCancel={onClose}
      hideFooter
      shouldRenderCloseIconButton
      title={attachment?.name ?? ''}
      containerClassName={styles.viewerModal}
    >
      {loadError ? (
        <p className={styles.viewerError}>
          <FormattedMessage
            id="attachmentViewer.loadError"
            defaultMessage="Could not load the file."
          />
        </p>
      ) : loading || pdfLoading ? (
        <div className={styles.viewerLoading}>
          <Spinner size={24} />
        </div>
      ) : kind === 'image' ? (
        <img
          className={styles.viewerImage}
          src={url}
          alt={attachment?.name ?? ''}
        />
      ) : kind === 'pdf' ? (
        <iframe
          className={styles.viewerPdf}
          src={pdfURL || url}
          title={attachment?.name ?? ''}
        />
      ) : kind === 'text' ? (
        <pre className={styles.viewerText}>{text ?? ''}</pre>
      ) : (
        <a
          className={styles.viewerDownload}
          data-component="open-attachment-link"
          href={url}
          target="_blank"
          rel="noreferrer"
        >
          <FormattedMessage
            id="attachmentViewer.open"
            defaultMessage="Open file"
          />
        </a>
      )}
    </Modal>
  );
};
